<template>
  <v-card class="base-card shadow">
    <v-card-title class="headline accent--text pb-4">
      {{ title }}
    </v-card-title>

    <v-card-text>
      <CardRow v-if="isEffContractVisibility">
        <v-checkbox
          class="rounded-lg mini-text field-edit d-inline-block"
          v-model="performance.commonWork.isEffContract"
          label="Работа входит в эффективный контракт"
          :disabled="isShowMode && !canChangeIsEffContractInShowMode"
          hide-details
          dense
          @change="updatePerformance"
        />
      </CardRow>

      <CardDateRow
        v-model="performance.reportDate"
        label="Дата доклада"
        placeholder="Добавьте дату доклада"
        :required="!isShowMode"
        :disabled="isShowMode"
        :error-messages="errorMessages.reportDate"
        @input="
          $v.performance.reportDate.$touch()
          updatePerformance()
        "
      />

      <CardAutocompleteRow
        v-model="performance.performanceType"
        :items="performanceTypeList"
        item-value="id"
        item-text="name"
        label="Тип выступления"
        placeholder="Выберите тип выступления"
        :disabled="isShowMode"
        :error-messages="errorMessages.performanceType"
        :required="!isShowMode"
        @input="
          $v.performance.performanceType.$touch()
          updatePerformance()
        "
      />

      <CardAutocompleteRow
        v-model="performance.eventType"
        :items="eventTypeList"
        item-value="id"
        item-text="name"
        label="Статус мероприятия"
        placeholder="Выберите статус мероприятия"
        return-object
        :disabled="isShowMode"
        :error-messages="errorMessages.eventType"
        :required="!isShowMode"
        @input="
          $v.performance.eventType.$touch()
          changeIsEffContract()
          updatePerformance()
        "
      />

      <CardAutocompleteRow
        v-model="performance.eventFormat"
        :items="eventFormatList"
        item-value="id"
        item-text="name"
        label="Формат мероприятия"
        placeholder="Выберите формат мероприятия"
        :disabled="isShowMode"
        :error-messages="errorMessages.eventFormat"
        :required="!isShowMode"
        @input="
          $v.performance.eventFormat.$touch()
          updatePerformance()
        "
      />

      <!--      <template v-if="participantQuantityVisibility">
        <CardAutocompleteRow
          v-model="performance.participantQuantity"
          :items="participantQuantityList"
          item-value="id"
          item-text="name"
          label="Общее число участников (Оценка)"
          placeholder="Выберите общее число участников"
          :disabled="isShowMode"
          :error-messages="errorMessages.participantQuantity"
          :required="!isShowMode"
          @input="$v.performance.participantQuantity.$touch(); updatePerformance()"
        />

        <CardFieldRow
          v-model="performance.representatives"
          type="number"
          label="Количество представителей университета"
          placeholder="Добавьте количество представителей университета"
          :disabled="isShowMode"
          :error-messages="errorMessages.representatives"
          :required="!isShowMode"
          @input="updatePerformance()"
        />
      </template>-->

      <CardFieldRow
        v-model="performance.performanceName"
        label="Наименование мероприятия"
        placeholder="Добавьте наименование мероприятия"
        :required="!isShowMode"
        :disabled="isShowMode"
        :error-messages="errorMessages.performanceName"
        @input="
          $v.performance.performanceName.$touch()
          updatePerformance()
        "
      />

      <CardFieldRow
        v-model="performance.commonWork.workName"
        label="Название доклада"
        placeholder="Добавьте название доклада"
        :required="!isShowMode"
        :disabled="isShowMode"
        :error-messages="errorMessages.workName"
        @input="
          $v.performance.commonWork.workName.$touch()
          updatePerformance()
        "
      />

      <div
        v-if="!isShowMode"
        class="d-flex"
      >
        <UIAlert
          title="Необходимо добавить ссылку на программу НПМ и/или прикрепить программу НПМ в электронном формате"
        />
      </div>

      <CardFieldRow
        v-model="performance.link"
        label="Ссылка на программу мероприятия"
        placeholder="Добавьте ссылку"
        hide-border-bottom
        :disabled="isShowMode"
        :error-messages="errorMessages.link"
        :required="!isShowMode"
        @input="
          $v.performance.link.$touch()
          updatePerformance()
        "
      />

      <CardRow
        label="Программа мероприятия"
        class="mt-0"
      >
        <UIUploadFile
          button-text="Добавить программу мероприятия в эл. формате"
          acceptFileFormats=".pdf, .doc, .docx"
          :maxFileWeightInMb="5"
          :is-show-mode="isShowMode"
          :multiple="true"
          :maxFiles="10"
          :fileList="linkFileList"
          @uploadFiles="uploadFiles"
          @removeFile="removeFile"
        />
      </CardRow>

      <CardTextAreaRow
        v-model="performance.remark"
        label="Примечание"
        placeholder="Добавьте примечание"
        hide-border-bottom
        :disabled="isShowMode"
        :error-messages="errorMessages.remark"
        @input="
          $v.performance.remark.$touch()
          updatePerformance()
        "
      />
    </v-card-text>
  </v-card>
</template>

<script>
import { Performance } from '@/models'
import CardRow from '@/components/ui/row/CardRow'
import CardFieldRow from '../../ui/row/CardFieldRow'
import { setValidateMessages } from '../../../scripts/validation'
import { required, maxLength, requiredIf } from 'vuelidate/lib/validators'
import { checkDate } from '@/scripts/validation'
import CardAutocompleteRow from '../../ui/row/CardAutocompleteRow'
import CardDateRow from '../../ui/row/CardDateRow'
import { mapGetters } from 'vuex'
import CardTextAreaRow from '@/components/ui/row/CardTextAreaRow.vue'
import UIAlert from '@/components/ui/UIAlert.vue'
import UIUploadFile from '@/components/ui/UIUploadFile.vue'
import { isEffContractMixin } from '@/mixins/isEffContract'

export default {
  components: { UIUploadFile, UIAlert, CardTextAreaRow, CardDateRow, CardAutocompleteRow, CardFieldRow, CardRow },
  props: {
    isShowMode: Boolean,
    selectedItem: Object,
    title: String,
    linkFileList: Array
  },

  mixins: [isEffContractMixin],

  mounted() {
    this.updateInvalid()
  },

  data: () => ({
    performance: new Performance(),

    eventTypeId: {
      internationalParticipation: 1,
      international: 2,
      allRussian: 3,
      regional: 4
    }
  }),

  validations() {
    return {
      performance: {
        commonWork: {
          workName: { required, maxLength: maxLength(1500) }
        },

        reportDate: { required, checkDate },
        performanceType: { required },
        eventType: { required },
        eventFormat: { required },
        /*        participantQuantity: { required: requiredIf(() => this.participantQuantityVisibility) },
        representatives: { required: requiredIf(() => this.participantQuantityVisibility) },*/
        performanceName: { required, maxLength: maxLength(300) },
        link: { required: requiredIf(() => !this.linkFileList.length), maxLength: maxLength(500) },
        remark: { maxLength: maxLength(300) }
      }
    }
  },

  computed: {
    ...mapGetters('dictionaries', ['performanceTypeList', 'eventTypeList', 'eventFormatList']),
    ...mapGetters('user', ['isAdmin', 'isModerator', 'isModeratorNir']),

    currentWork() {
      return this.performance
    },

    participantQuantityVisibility() {
      return [this.eventTypeId.allRussian, this.eventTypeId.regional].includes(this.performance?.eventType?.id)
    },

    errorMessages() {
      const allErrors = {}

      Object.keys(this.$v.performance.$params).forEach(key => {
        allErrors[key] = setValidateMessages(this.$v.performance[key])
      })

      Object.keys(this.$v.performance.commonWork.$params).forEach(key => {
        allErrors[key] = setValidateMessages(this.$v.performance.commonWork[key])
      })

      return allErrors
    }
  },

  methods: {
    updateInvalid() {
      this.$emit('updateInvalid', this.$v.$invalid)
    },

    changeIsEffContract() {
      if (this.participantQuantityVisibility) this.performance.commonWork.isEffContract = false
      else this.performance.commonWork.isEffContract = true
    },

    updatePerformance() {
      this.$emit('update:selected-item', new Performance(this.performance.getForAPI()))
      this.updateInvalid()
    },

    uploadFiles(files) {
      this.$emit('uploadLinkFiles', files)
      this.updatePerformance()
    },

    removeFile(index) {
      this.$emit('removeLinkFile', index)
      this.updatePerformance()
    }
  },

  watch: {
    selectedItem: {
      immediate: true,
      deep: true,
      handler(val) {
        this.performance = new Performance(val.getForAPI())
        this.updateInvalid()
      }
    }
  }
}
</script>

<style scoped></style>
